import { useEffect, useRef } from 'react'
import { useMatch, useRouterState } from '@tanstack/react-router'
import { NOT_FOUND_HEADING, navEntryFor, normalizeRouteId, type NavEntry, type NavItem, type NavModel } from './model'

// These hooks read a route's fullPath ('/seasons/$seasonId/games/generate'),
// never location.pathname: the router moves the location to the next URL as
// soon as a navigation starts and commits the next matches only once the
// destination has loaded, so the leaving page would re-render with the next
// page's URL (a wrong heading, or in DEV a throw on a page with no nav entry).

// Vite replaces import.meta.env at build time. Read through a cast so this
// package needs no vite/client types of its own.
const isDev = (): boolean => (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true

/** The route id of the deepest committed match: what is on screen now. */
export function useCommittedRouteId(): string | undefined {
  return useRouterState({ select: (s): string | undefined => s.matches.at(-1)?.fullPath })
}

/** True while the router shows a not-found page: an address no route matches
 *  or a loader that threw notFound(). ⚠️ `_notFound` is TanStack's own flag
 *  for the unmatched case (typed on its match, router-core 1.171); the only
 *  read of it is here, and NotFoundPage.test.tsx fails if an upgrade moves it. */
export function useIsNotFound(): boolean {
  return useRouterState({ select: (s): boolean => s.matches.some((x) => x._notFound === true || x.status === 'notFound') })
}

/** The nav entry for the page on screen, for the shell (breadcrumb, tab title). */
export function useNavEntryFor<I extends NavItem>(m: NavModel<I>): NavEntry<I> | null {
  const routeId = useCommittedRouteId()
  return routeId ? navEntryFor(m, routeId) : null
}

/** The current page's heading: its nav label, so the menu and the page can
 *  never disagree (each app's drift test enforces that every page uses this).
 *  It reads the calling route's own match, so it cannot pick up another page's
 *  label; a layout with no entry of its own falls back to the committed leaf. */
export function useNavTitleFor<I extends NavItem>(m: NavModel<I>): string {
  const ownId = useMatch({ strict: false, shouldThrow: false, select: (x): string => x.fullPath })
  const leafId = useCommittedRouteId()
  // No own match means this route is leaving (its match clears a moment before
  // it unmounts): keep the label it had rather than read the next page's.
  const entry = ownId ? (navEntryFor(m, ownId) ?? (leafId ? navEntryFor(m, leafId) : null)) : null
  const last = useRef(entry)
  if (entry) last.current = entry
  if (!last.current) {
    if (isDev()) throw new Error('useNavTitle on a route with no nav entry')
    return ''
  }
  return last.current.item.label
}

/** "{heading} · Region 13 {app}", or "Region 13 {app}" with no heading. */
export function documentTitle(m: Pick<NavModel, 'app'>, heading: string | null): string {
  return heading ? `${heading} · Region 13 ${m.app}` : `Region 13 ${m.app}`
}

/** Sets document.title when `title` changes. Each app's shell calls it with
 *  documentTitle(nav, heading); a child page calls it with its own heading. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = title
  }, [title])
}

/** The shell's tab title. On a nav page: "{label} · Region 13 {app}". On a
 *  declared child route it sets nothing: the child page owns its title, and a
 *  write here would land after the child's own effect (a parent's effects run
 *  after its children's) and overwrite it. Anywhere else, or signed out, the
 *  bare "Region 13 {app}". On a not-found page, "Page not found · Region 13 {app}". */
export function useShellTitle<I extends NavItem>(m: NavModel<I>, opts?: { signedIn?: boolean }): void {
  const routeId = useCommittedRouteId()
  const id = routeId ? normalizeRouteId(routeId) : null
  const entry = id ? navEntryFor(m, id) : null
  const isChild = id !== null && Object.keys(m.childRoutes).some((c) => normalizeRouteId(c) === id)
  const notFound = useIsNotFound()
  let title: string | null
  if (opts?.signedIn === false) title = documentTitle(m, null)
  else if (notFound) title = documentTitle(m, NOT_FOUND_HEADING)
  else if (entry) title = documentTitle(m, entry.item.label)
  else title = isChild ? null : documentTitle(m, null)
  useEffect(() => {
    if (title !== null) document.title = title
  }, [title])
}
